import { useQuery } from "@apollo/client";
import NavbarLayout from "components/layouts/NavbarLayout";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import classNames from "components/ui/classNames";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import getYoutubeEmbedUrl from "components/utils/getEmbedUrl";
import getYear from "date-fns/getYear";
import { useAmplitude } from "lib/amplitude/Amplitude";
import YoutubePlaylistByIdQuery from "lib/queries/YoutubePlaylistByIdQuery";
import type {
  IYoutubePlaylistByIdQuery,
  IYoutubePlaylistByIdQueryVariables,
} from "lib/queries/__generated__/YoutubePlaylistByIdQuery.generated";
import formatTimeFromSeconds from "lib/utils/formatTimeFromSeconds";
import getBaseUrl from "lib/utils/getBaseUrl";
import getQueryStringValue from "lib/utils/getQueryStringValue";
import { ChevronLeft, Instagram, PauseCircleIcon, PlayCircleIcon, Twitter, Youtube } from "lucide-react";
import type { NextPage } from "next";
import Link from "next/link";
import { getVideoHref } from "pages/irpf";
import { useMemo } from "react";
import { jsonLdScriptProps } from "react-schemaorg";
import type { VideoObject } from "schema-dts";

interface IIRPFHowToPageProps {
  videoId: string | null;
  chapter: string | null;
}

const IRPFHowToPage: NextPage<IIRPFHowToPageProps> = function IRPFHowToPage({ videoId, chapter }: IIRPFHowToPageProps) {
  const amplitude = useAmplitude();
  const year = getYear(new Date());

  const { data, loading } = useQuery<IYoutubePlaylistByIdQuery, IYoutubePlaylistByIdQueryVariables>(
    YoutubePlaylistByIdQuery,
    {
      variables: {
        playlistId: "PLHAwhClPYLzSZcBfYR38sXZcuvyBfOvR6",
      },
    },
  );

  const videoToShow = useMemo(() => {
    if (!videoId || !data || !data.youtubePlaylistById) {
      return null;
    }

    const [firstVideo] = data.youtubePlaylistById;
    const video = data.youtubePlaylistById.find((candidate) => candidate.video.id === videoId);

    if (!video) {
      return null;
    }

    const isFirstVideo = video.id === firstVideo.id;

    if (chapter) {
      const videoChapter = video.video.chapters?.find((candidate) => candidate.slug === chapter);

      if (!videoChapter) {
        return null;
      }

      const { title, url, start, end } = videoChapter;

      amplitude.logEvent("Viewed IRPF video", {
        id: videoId,
        title,
        url,
        start,
        end,
      });

      return {
        id: videoId,
        title,
        url: getYoutubeEmbedUrl(videoId, { autoplay: true, start, end }),
      };
    }

    amplitude.logEvent("Viewed IRPF video", {
      id: videoId,
      title: video.video.title,
      url: video.url,
    });

    return {
      id: videoId,
      title: video.video.title,
      url: getYoutubeEmbedUrl(videoId, { autoplay: !isFirstVideo }),
    };
  }, [videoId, chapter, data, amplitude]);

  const pageTitle = videoToShow ? `${videoToShow?.title} | IRPF ${year} | Fundamentei` : `IRPF ${year} | Fundamentei`;

  return (
    <NavbarLayout>
      <FundamenteiMetadata
        title={pageTitle}
        description="Se você investe na Bolsa de Valores, é importante saber como declarar corretamente seus investimentos no Imposto de Renda (IRPF), veja aqui o passo a passo..."
      />

      <div
        className={classNames(
          "mx-auto grid w-full max-w-7xl grid-cols-4 gap-x-4 gap-y-6 px-4 py-6",
          "xl:grid-cols-12 xl:gap-9 xl:gap-y-8 xl:px-0 xl:py-8 xl:pb-12",
        )}
      >
        <div className="col-span-4 xl:col-span-12">
          <Link
            href="/irpf"
            as="/irpf"
            passHref={true}
            className="flex items-center space-x-1 font-aeonik text-base font-bold text-blue-600"
          >
            <ChevronLeft size={18} />
            Voltar para IRPF{year}
          </Link>
        </div>

        {loading && <YoutubeVideoSkeleton />}
        {videoToShow && (
          <div className="col-span-4 aspect-video w-full rounded-xl border border-neutral-200 bg-white p-4 shadow-sm xl:col-span-8">
            <iframe
              title={videoToShow.title}
              className="h-full w-full"
              allowFullScreen={true}
              allow="autoplay"
              src={videoToShow.url}
              frameBorder="0"
            />
          </div>
        )}

        {loading && <PlaylistSkeleton />}
        <div
          className="col-span-4 flex h-[480px] w-auto flex-col space-y-4 overflow-y-scroll pb-5 pr-4"
          style={{
            WebkitMaskImage: "linear-gradient(to bottom, black calc(100% - 48px), transparent 100%)",
          }}
        >
          <div className="mb-2 flex flex-col font-aeonik">
            <h2 className="m-0 text-lg font-bold text-gray-900">Imposto de Renda {year}</h2>
            {data && data.youtubePlaylistById && (
              <span className="font-normal text-gray-600">{data.youtubePlaylistById.length} aulas</span>
            )}
          </div>

          {data &&
            data.youtubePlaylistById &&
            data.youtubePlaylistById.map((node) => (
              <div key={node.video.title} className="flex flex-col space-y-3">
                <Link
                  href="/irpf/[videoId]"
                  as={getVideoHref(node)}
                  replace={true}
                  passHref={true}
                  className="grid grid-cols-[auto_1fr_auto] items-center gap-x-3"
                >
                  <div className="flex h-5 w-5 items-center justify-center bg-stone-100 px-2 py-1 squircle">
                    <span className="font-aeonik text-xs font-bold text-slate-500">{node.position + 1}</span>
                  </div>
                  <TooltipProvider delayDuration={700}>
                    <Tooltip>
                      <TooltipTrigger asChild={true}>
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-left font-aeonik text-sm font-bold text-gray-600">
                          {node.video.title}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        sideOffset={12}
                        className="max-w-xs rounded-xl border border-transparent bg-black font-aeonik font-medium text-white"
                      >
                        {node.video.title}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {!chapter && videoToShow?.id === node.video.id ? (
                    <PauseCircleIcon className="h-6 w-6 fill-stone-700 text-white" />
                  ) : (
                    <PlayCircleIcon className="h-6 w-6 fill-blue-600 text-white" />
                  )}
                </Link>

                <hr className="m-0 w-full border-t border-t-slate-200" />

                {videoToShow &&
                  node.video.id === videoToShow.id &&
                  node.video.chapters &&
                  node.video.chapters.length > 0 && (
                    <ol className="flex flex-col space-y-3 font-aeonik text-sm font-medium text-gray-500">
                      {node.video.chapters.map((chapter, index) => {
                        const isActive = videoToShow.id === node.video.id && chapter.title === videoToShow.title;
                        return (
                          <li key={chapter.title} className="w-full">
                            <Link
                              href={getVideoHref(node, chapter)}
                              as={getVideoHref(node, chapter)}
                              passHref={true}
                              className="grid w-full grid-cols-[1fr_auto] items-center gap-x-3"
                            >
                              <TooltipProvider delayDuration={700}>
                                <Tooltip>
                                  <TooltipTrigger asChild={true}>
                                    <span
                                      className={classNames(
                                        "overflow-hidden text-ellipsis whitespace-nowrap text-left font-aeonik",
                                        "text-sm font-medium text-gray-500",
                                        {
                                          "font-bold text-blue-600": isActive,
                                        },
                                      )}
                                    >
                                      {index + 1}. {chapter.title}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="top"
                                    sideOffset={12}
                                    className="max-w-xs rounded-xl border border-transparent bg-black font-aeonik font-medium text-white"
                                  >
                                    {index + 1}. {chapter.title}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <div className="flex items-center space-x-4">
                                <span>{formatTimeFromSeconds(chapter.durationInSeconds, "short")}</span>

                                {isActive ? (
                                  <PauseCircleIcon className="h-6 w-6 fill-stone-700 text-white" />
                                ) : (
                                  <PlayCircleIcon className="h-6 w-6 fill-neutral-400" />
                                )}
                              </div>
                            </Link>
                          </li>
                        );
                      })}
                    </ol>
                  )}
              </div>
            ))}
        </div>

        <div className="col-span-4 xl:col-span-8">
          <h2 className="py-2 font-aeonik text-2xl font-medium text-gray-900">Sobre o autor</h2>

          <div className="mb-6 mt-4 flex space-x-4">
            <div className="h-14 w-14 overflow-hidden rounded-full bg-neutral-200 ring-2 ring-red-500 ring-offset-2">
              <img
                src={`${getBaseUrl()}/static/people/arthur.png`}
                alt="Arthur “Asvid” Svidzinski"
                className="aspect-square object-cover"
              />
            </div>
            <div className="flex flex-col space-y-0.5 font-aeonik">
              <h3 className="text-lg font-bold text-gray-900">Arthur “Asvid” Svidzinski</h3>
              <span className="text-base font-normal text-gray-500">CNPI nº 2411</span>
            </div>
          </div>

          <div className="flex flex-col space-y-6 font-aeonik text-base font-normal leading-8 tracking-wide text-gray-600">
            <p>
              Meu nome é Arthur, sou Analista de Valores Mobiliários, e desde 2019 atuo de forma ativa no meu canal do
              YouTube ajudando pessoas que desejam aprender mais sobre Análise de Fundamentalista de Ações, Stocks e
              REITs, visando escolher os melhores investimentos para uma carteira de longo prazo focada no método
              Buy&Hold.
            </p>

            <p>
              Eu declaro por conta própria meus investimentos na Bolsa de Valores há mais de 15 anos, então vendo a
              dificuldade que diversos investidores têm anualmente na época da declaração, decidi gravar essa série de
              vídeos para explicar passo a passo como declarar seus investimentos da forma correta, desde o básico até o
              avançado, sempre baseado nos normativos e instruções da Receita Federal.
            </p>

            <p>
              Com um pouco de esforço e dedicação você verá que é plenamente possível declarar todos os seus
              investimentos em Renda Variável com segurança, praticamente zerando seu risco de cair na Malha Fina.
            </p>

            <p>Um grande abraço,</p>

            <p className="flex flex-col">
              <strong>Arthur Estivalet Svidzinski</strong>
              <span>Analista CNPI nº 2411</span>
            </p>

            <div className="flex items-center space-x-3">
              <a href="https://twitter.com/CanaldoASVID" target="_blank" rel="noreferrer noopener">
                <Twitter className="h-6 w-6 hover:text-brand" />
              </a>

              <a href="https://www.youtube.com/c/CanaldoASVID" target="_blank" rel="noreferrer noopener">
                <Youtube className="h-6 w-6 hover:text-brand" />
              </a>

              <a href="https://www.instagram.com/canaldoasvid/" target="_blank" rel="noreferrer noopener">
                <Instagram className="h-6 w-6 hover:text-brand" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {data &&
        data.youtubePlaylistById &&
        data.youtubePlaylistById.map((node) => (
          <script
            key={node.video.title}
            {...jsonLdScriptProps<VideoObject>({
              "@context": "https://schema.org",
              "@type": "VideoObject",
              name: node.video.title,
              description: node.video.title,
              thumbnailUrl: [node.video.thumbnails.default.url],
              embedUrl: getYoutubeEmbedUrl(node.video.id),
              uploadDate: node.video.publishedAt,
            })}
          />
        ))}
    </NavbarLayout>
  );
};

export default IRPFHowToPage;

IRPFHowToPage.getInitialProps = async ({ query }) => ({
  videoId: getQueryStringValue(query, "videoId"),
  chapter: getQueryStringValue(query, "chapter"),
});

function YoutubeVideoSkeleton() {
  return (
    <div className="col-span-4 h-80 w-full animate-pulse rounded-xl border border-neutral-200 bg-slate-100 p-4 shadow-sm md:h-[444px] xl:col-span-8" />
  );
}

function PlaylistSkeleton() {
  return (
    <div className="col-span-4 flex h-full w-auto animate-pulse flex-col space-y-6 rounded bg-slate-100 p-2">
      <div className="space-y-0.5">
        <div className="h-6 w-44 rounded bg-slate-300" />
        <div className="h-5 w-36 rounded bg-slate-300" />
      </div>

      <div className="mt-6 space-y-3">
        <div className="h-6 w-full rounded bg-slate-300" />
        <div className="h-6 w-full rounded bg-slate-300" />
        <div className="h-6 w-full rounded bg-slate-300" />
        <div className="h-6 w-full rounded bg-slate-300" />
        <div className="h-6 w-full rounded bg-slate-300" />
        <div className="h-6 w-full rounded bg-slate-300" />
        <div className="h-6 w-full rounded bg-slate-300" />
        <div className="h-6 w-full rounded bg-slate-300" />
        <div className="h-6 w-full rounded bg-slate-300" />
        <div className="h-6 w-full rounded bg-slate-300" />
      </div>
    </div>
  );
}
